import { Nullable } from '@rotomeca/utils';
import { Disposable } from './types';

/**
 * Classe de gestion de planification d'exécution de callback.
 * Permet de regrouper plusieurs appels en une seule exécution lors du prochain frame.
 */
export class Scheduler<T> implements Disposable {
	/**
	 * Indique si une exécution est déjà planifiée.
	 * @private
	 */
	#_started: boolean = false;

	/**
	 * Dernière valeur planifiée pour l'exécution.
	 * @private
	 */
	#_lastValue: Nullable<T> = null;

	/**
	 * Identifiant du frame planifié.
	 * @private
	 */
	#_frameId: Nullable<number> = null;

	/**
	 * Callback à exécuter lors de la planification.
	 * @private
	 */
	#_callback: Nullable<(value: Nullable<T>) => void>;

	/**
	 * Constructeur de la classe Scheduler.
	 * @param callback Sera appelée avec la dernière valeur planifiée lors du prochain frame.
	 */
	constructor(callback: (value: Nullable<T>) => void) {
		this.#_callback = callback;
	}

	/**
	 * Libère les ressources du scheduler.
	 */
	public dispose(): void {
		if (this.#_frameId !== null) {
			cancelAnimationFrame(this.#_frameId);
			this.#_frameId = null;
		}
		this.#_callback = null;
		this.#_lastValue = null;
		this.#_started = false;
	}

	/**
	 * Demande la planification de l'exécution de la callback avec la valeur donnée.
	 * Si une exécution est déjà planifiée, seule la dernière valeur sera utilisée.
	 * @param value Valeur la plus récente planifiée pour l'exécution.
	 */
	public schedule(value: T): void {
		this.#_lastValue = value;

		if (!this.#_started) {
			this.#_started = true;
			this.#_frameId = requestAnimationFrame(() => {
				this.#_frameId = null;
				this.#_callback?.(this.#_lastValue);
				this.#_started = false;
				this.#_lastValue = null;
			});
		}
	}

	/**
	 * Accesseur protégé pour obtenir la dernière valeur planifiée.
	 */
	protected get _p_value(): Nullable<T> {
		return this.#_lastValue;
	}

	/**
	 * Accesseur protégé pour définir la dernière valeur planifiée.
	 */
	protected set _p_value(value: Nullable<T>) {
		this.#_lastValue = value;
	}

	/**
	 * Appelle immédiatement la callback avec la valeur donnée, sans planification.
	 * @param value Valeur à transmettre au callback
	 */
	public call(value: T): void {
		this.#_callback?.(value);
	}
}

/**
 * Variante de Scheduler pour gérer des tableaux ou des symboles de réinitialisation.
 *
 * Permet de regrouper plusieurs appels en une seule exécution lors du prochain frame.
 *
 * Si jamais une réinitialisation est demandée, le tableau sera vidé avant d'ajouter de nouveaux éléments.
 */
export class SchedulerArray<T> implements Disposable {
	/**
	 * Indique si une exécution est déjà planifiée.
	 * @private
	 */
	#_started: boolean = false;

	/**
	 * Symbole utilisé pour réinitialiser le tableau.
	 * @private
	 */
	#_resetSymbol: symbol;
	/**
	 * Pile des éléments planifiés.
	 * @private
	 */
	#_stack: (T | T[] | symbol)[] = [];
	/**
	 * Identifiant du frame planifié.
	 * @private
	 */
	#_frameId: Nullable<number> = null;

	/**
	 * Callback à exécuter lors de la planification.
	 * @private
	 */
	#_callback: Nullable<(value: Nullable<T[] | symbol>) => void>;

	/**
	 * Constructeur de la classe SchedulerArray.
	 * @param callback Fonction appelée lors de la planification.
	 * @param resetSymbol Symbole utilisé pour réinitialiser le tableau.
	 */
	constructor(
		callback: (value: Nullable<T[] | symbol>) => void,
		resetSymbol: symbol,
	) {
		this.#_callback = callback;
		this.#_resetSymbol = resetSymbol;
	}

	/**
	 * Libère les ressources du scheduler.
	 */
	public dispose(): void {
		if (this.#_frameId !== null) {
			cancelAnimationFrame(this.#_frameId);
			this.#_frameId = null;
		}
		this.#_callback = null;
		this.#_stack.length = 0;
		this.#_started = false;
	}

	/**
	 * Demande la planification de l'exécution de la callback avec la valeur donnée.
	 * Peut recevoir un tableau, un élément ou un symbole de réinitialisation.
	 * @param value Valeur la plus récente planifiée pour l'exécution.
	 */
	public schedule(value: T[]): void;
	public schedule(value: T): void;
	public schedule(value: symbol): void;
	public schedule(value: T | symbol): void;
	public schedule(value: T[] | symbol): void;
	public schedule(value: symbol | T | T[]): void {
		this.#_add(value);

		if (!this.#_started) {
			this.#_started = true;
			this.#_frameId = requestAnimationFrame(() => {
				this.#_frameId = null;
				if (this.#_callback) {
					for (const element of this.#_getStackItems()) {
						this.#_callback(element);
					}
				}

				this.#_started = false;
				this.#_stack.length = 0;
			});
		}
	}

	/**
	 * Appelle immédiatement la callback avec la valeur donnée, sans planification.
	 *
	 * La stack en mémoire est utilisé si aucune valeur n'est fournie. Sinon, elle sera vidée avant d'ajouter la nouvelle valeur.
	 * @param value Valeur à transmettre au callback
	 */
	public call(value: Nullable<T | T[] | symbol>): void {
		if (value !== null) {
			this.#_stack.length = 0;
			this.#_add(value!);
		}

		if (this.#_callback) {
			for (const element of this.#_getStackItems()) {
				this.#_callback(element);
			}
		}

		this.#_stack.length = 0;
	}

	/**
	 * Ajoute une valeur ou un tableau de valeurs à la pile, ou gère le symbole de réinitialisation.
	 * @param value Valeur, tableau de valeurs ou symbole de réinitialisation à ajouter.
	 * @returns void
	 */
	#_add(value: symbol | T | T[]): void {
		// Gestion du symbole de réinitialisation
		if (value === this.#_resetSymbol) {
			this.#_stack.length = 0;
			this.#_stack.push(value);
			this.#_stack.push([]);
			return;
		}

		// Initialisation de la pile si vide
		if (this.#_stack.length === 0) this.#_stack.push([]);

		// Ajout de l'élément ou des éléments au dernier tableau de la pile
		if (Array.isArray(value)) {
			(this.#_stack[this.#_stack.length - 1] as T[]).push(...value);
		} else if (value !== this.#_resetSymbol) {
			(this.#_stack[this.#_stack.length - 1] as T[]).push(value as T);
		}
	}

	/**
	 * Générateur pour obtenir les éléments de la pile un par un.
	 *
	 * Gère les tableaux et le symbole de réinitialisation.
	 * @returns Générateur d'éléments de type T[] ou symbol.
	 */
	*#_getStackItems(): Generator<T[] | symbol, void, unknown> {
		for (const element of this.#_stack) {
			if (element === this.#_resetSymbol) {
				yield element as symbol;
			} else if (Array.isArray(element)) {
				yield element;
			} else {
				yield [element as T];
			}
		}
	}
}
